import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchWithAuth } from "@/lib/api/client";
import { showToast } from "@/lib/toast";

export interface InvoiceOut {
  id: string;
  document_id: string;
  proveedor: string | null;
  nif: string | null;
  numero: string | null;
  fecha: string | null;
  base_imponible: number | null;
  iva: number | null;
  total: number | null;
  moneda: string | null;
  obra: string | null;
}

export interface InvoiceListOut {
  items: InvoiceOut[];
  count: number;
  total_base_imponible: number;
  total_total: number;
}

export interface ExtractResult {
  procesados: number;
  facturas: number;
  no_facturas: number;
  omitidos_sin_texto: number;
}

export interface InvoiceLine {
  descripcion: string | null;
  cantidad: number | null;
  unidad: string | null;
  precio_unit: number | null;
  importe: number | null;
}

export const invoiceKeys = {
  list: (projectId: string) => ["invoices", "list", projectId] as const,
  lines: (projectId: string, documentId: string) =>
    ["invoices", "lines", projectId, documentId] as const,
};

export function useInvoiceLines(
  projectId: string,
  documentId: string,
  enabled: boolean
) {
  return useQuery<InvoiceLine[], Error>({
    queryKey: invoiceKeys.lines(projectId, documentId),
    queryFn: () =>
      fetchWithAuth(`/api/v1/projects/${projectId}/invoices/${documentId}/lines`),
    enabled: enabled && !!projectId && !!documentId,
  });
}

export function useInvoices(projectId: string) {
  return useQuery<InvoiceListOut, Error>({
    queryKey: invoiceKeys.list(projectId),
    queryFn: () => fetchWithAuth(`/api/v1/projects/${projectId}/invoices`),
    enabled: !!projectId,
  });
}

export function useExtractInvoices(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<ExtractResult, Error, { force?: boolean } | void>({
    mutationFn: (vars) => {
      const force = vars && "force" in vars ? vars.force : false;
      const qs = force ? "?force=true" : "";
      return fetchWithAuth(
        `/api/v1/projects/${projectId}/invoices/extract${qs}`,
        { method: "POST" }
      );
    },
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.list(projectId) });
      showToast.success(
        `Extracción: ${r.facturas} facturas de ${r.procesados} documentos`
      );
    },
    onError: (e) => showToast.error(e.message || "Error al extraer facturas"),
  });
}
